import React, { useContext, useEffect, useId, useState } from 'react';
import { Collapse, Box, Button, forwardRef } from '@chakra-ui/react';
import { ActionMenuContext } from './action-menu-context';
import { ChevronDownIcon as AccordionIcon } from '@allergan-data-labs/alle-elements-icons/20/chevronDownIcon';

import {
  getActionMenuAccordionIconStyles,
  getActionMenuAccordionButtonStyles,
  getActionMenuAccordionPanelStyles,
} from './action-menu-accordion.theme';
import {
  ActionMenuAccordionContext,
  ActionMenuAccordionItemContext,
} from './action-menu-accordion-context';

interface ActionMenuAccordionProps {
  children?: React.ReactNode;
}
interface ActionMenuItemProps {
  children?: React.ReactNode;
  isDisabled?: boolean;
}

interface ActionMenuAccordionButtonProps {
  children?: React.ReactNode;
}

interface ActionMenuAccordionPanelProps {
  children?: React.ReactNode;
}

/**
 * The ActionMenuAccordion adds accordion behavior (ability to expand/collapse sections to reveal more links) to the ActionMenu.
 * The ActionMenuAccordion* components will help implement this behavior.
 */
export const ActionMenuAccordion = forwardRef<ActionMenuAccordionProps, 'div'>(
  ({ children, ...rest }, ref) => {
    const [openId, setOpenId] = useState('');

    return (
      <ActionMenuAccordionContext.Provider value={{ openId, setOpenId }}>
        <Box ref={ref} {...rest}>
          {children}
        </Box>
      </ActionMenuAccordionContext.Provider>
    );
  }
);

export const ActionMenuAccordionItem = forwardRef<ActionMenuItemProps, 'div'>(
  ({ children, isDisabled = false, ...rest }, ref) => {
    const { openId } = useContext(ActionMenuAccordionContext);
    const uniqueId = useId();
    const id = `actionMenuAccordionItem:${uniqueId}`;
    const [isOpen, setIsOpen] = useState(() => openId === id);

    useEffect(() => {
      if (openId !== id) {
        setIsOpen(false);
      }
    }, [openId, id]);

    return (
      <ActionMenuAccordionItemContext.Provider
        value={{ id, isDisabled, isOpen, setIsOpen }}
      >
        <Box ref={ref} id={id} {...rest}>
          {children}
        </Box>
      </ActionMenuAccordionItemContext.Provider>
    );
  }
);

export const ActionMenuAccordionButton = forwardRef<
  ActionMenuAccordionButtonProps,
  'button'
>(({ children, ...rest }, ref) => {
  const { colorMode } = useContext(ActionMenuContext);
  const { id, isDisabled, isOpen, setIsOpen } = useContext(
    ActionMenuAccordionItemContext
  );
  const buttonId = `actionMenuAccordionItem:${id}:button`;
  const panelId = `actionMenuAccordionItem:${id}:panel`;
  const { setOpenId } = useContext(ActionMenuAccordionContext);

  const onClickHandler = () => {
    if (isDisabled) {
      return;
    }

    setIsOpen(!isOpen);
    setOpenId(id);
  };

  const classNames = 'action-menu-accordion__button';

  return (
    <Button
      ref={ref}
      id={buttonId}
      aria-controls={panelId}
      className={classNames}
      onClick={onClickHandler}
      isDisabled={isDisabled}
      __css={getActionMenuAccordionButtonStyles(colorMode)}
      {...rest}
    >
      {children}
      <AccordionIcon
        className="action-menu-accordion__icon"
        sx={getActionMenuAccordionIconStyles(isOpen)}
      />
    </Button>
  );
});

export const ActionMenuAccordionPanel = forwardRef<
  ActionMenuAccordionPanelProps,
  'div'
>(({ children, ...rest }, ref) => {
  const { isOpen, id } = useContext(ActionMenuAccordionItemContext);
  const buttonId = `actionMenuAccordionItem:${id}:button`;
  const panelId = `actionMenuAccordionItem:${id}:panel`;
  return (
    <Collapse in={isOpen}>
      <Box
        ref={ref}
        id={panelId}
        aria-labelledby={buttonId}
        sx={getActionMenuAccordionPanelStyles()}
        {...rest}
      >
        {children}
      </Box>
    </Collapse>
  );
});
